import { Box, IconButton, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png';

const AboutUs = () => {
  const [aboutData, setAboutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    history: '',
    short_description: '',
    steam_benefits: '',
    steam_means: '',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php','about_us'); // Fetch About Us data
        if (response.success) {
          setAboutData(response.data);
        } else {
          toast.error(response.message || "Failed to load About Us data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch About Us data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      history: '',
      short_description: '',
      steam_benefits: '',
      steam_means: '',
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setFormData({
      history: item.history || '',
      short_description: item.short_description || '',
      steam_benefits: item.steam_benefits || '',
      steam_means: item.steam_means || '',
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php','about_us', itemToDelete.id); // Delete About Us entry by ID
      if (response.success) {
        toast.success(`About Us entry deleted successfully`);
        setAboutData(aboutData.filter(item => item.id !== itemToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete About Us entry");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete About Us entry");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedItem) {
        // Update existing entry
        response = await updateData('proccess.php','about_us',  formData, selectedItem.id); // Update About Us entry
        if (response.success) {
          toast.success(`About Us entry updated successfully`);
          setAboutData(aboutData.map(item => 
            item.id === selectedItem.id ? { ...item, ...formData } : item
          ));
        }
      } else {
        // Add new entry
        response = await addData('proccess.php','about_us', formData); // Add About Us entry
        if (response.success) {
          toast.success(`About Us entry added successfully`);
          setAboutData([...aboutData, response.data]);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedItem(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { 
      field: "history", 
      headerName: "History", 
      flex: 1, 
      minWidth: 120
    },
    { 
      field: "short_description", 
      headerName: "Short Description", 
      flex: 1,
      minWidth: 120
    },
    { 
      field: "steam_benefits", 
      headerName: "STEAM Benefits", 
      flex: 1, 
      minWidth: 120
    },
    { 
      field: "steam_means", 
      headerName: "What STEAM Means", 
      flex: 1, 
      minWidth: 120
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton 
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img 
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedItem(null);
        }} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: "none",
          }
        }}
      >
        <DialogTitle sx={{ color: colors.dark[400] }}>
          {selectedItem ? "Edit About Us Entry" : "Add New About Us Entry"}
        </DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} flexDirection={"column"} gap={"1rem"} width={"100%"} mb={"1rem"}>
              <TextField
                fullWidth
                id="history"
                name="history"
                label="History"
                multiline
                minRows={4}
                maxRows={12}
                variant="outlined"
                value={formData.history}
                onChange={handleFormChange}
                required
              />
              <TextField
                fullWidth
                id="short_description"
                name="short_description"
                label="Short Description"
                multiline
                minRows={4}
                maxRows={12}
                variant="outlined"
                value={formData.short_description}
                onChange={handleFormChange}
                required
              />
              <TextField
                fullWidth
                id="steam_benefits"
                name="steam_benefits"
                label="STEAM Benefits"
                multiline
                minRows={4}
                maxRows={12}
                variant="outlined"
                value={formData.steam_benefits}
                onChange={handleFormChange}
                required
              />
              <TextField
                fullWidth
                id="steam_means"
                name="steam_means"
                label="What STEAM Means"
                multiline
                minRows={4}
                maxRows={12}
                variant="outlined"
                value={formData.steam_means}
                onChange={handleFormChange}
                required
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
            }}
            sx={{ color: colors.dark[400] }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedItem ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this About Us entry?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm}
            sx={{
              backgroundColor: colors.red[400],
              '&:hover': {
                backgroundColor: colors.red[500],
              }
            }} 
            variant="contained" 
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
          <Box flex={1}>
              <Typography
                  variant="h4"
                  color={colors.dark[400]}
                  fontWeight={"bold"}
                  sx={{mb: "5px"}}
              >About Us Management</Typography>
              <Typography variant="h5" color={colors.blue[400]}>Manage About Us content</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Entry
          </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor = {colors.dark[300]} padding={".2rem"} mt={"1rem"}>
          <DataGrid
            rows={aboutData}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            sx={{
                border: "none",
                ".MuiDataGrid-cell": {
                  borderBottom: "none",
                  backgroundColor: "none"
                },
                ".MuiDataGrid-toolbarContainer": {
                  padding: "10px",
                  backgroundColor: "none",
                  borderBottom: "none",
                },
                ".MuiDataGrid-quickFilter": {
                  padding: "8px",
                  borderRadius: "4px",
                  border: `1px solid ${colors.primary[100]}`,
                },
                ".MuiDataGrid-columnHeaders": {
                  backgroundColor: "none",
                },
                ".MuiDataGrid-footerContainer": {
                  backgroundColor: "none",
                },
                ".MuiDataGrid-virtualScroller": {
                  backgroundColor: "none",
                },
            }}
            slots={{
                toolbar: () => (
                <div>
                    <GridToolbarQuickFilter />
                </div>
                ),
                noRowsOverlay: NoDataOverlay,
            }}
            slotProps={{
                toolbar: {
                showQuickFilter: true,
                quickFilterProps: {
                    debounceMs: 500,
                    placeholder: "Search...",
                },
                },
            }}
          />
      </Box>
    </Box>
  );
};

export default AboutUs;